import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { formatAddress, toTitleCase } from "@utils/utils";
import React from "react";
import { useNavigate } from "react-router-dom";

import { Button, Label, Paragraph } from "@fronterahealth/frontera-ui-components";

import { ClinicListingType } from "@api/graphql/types-and-hooks";
import { useCareNavigationVerifyOrgListingCreate } from "@api/rest/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

interface SelectedClinicProps {
  clinic: ClinicListingType;
  setSelectedClinic: React.Dispatch<React.SetStateAction<ClinicListingType | null>>;
}

export const SelectedClinic: React.FC<SelectedClinicProps> = ({ clinic, setSelectedClinic }) => {
  const navigate = useNavigate();
  const { mutateAsync, isPending } = useCareNavigationVerifyOrgListingCreate();

  const handleSelect = async () => {
    try {
      const response = await mutateAsync({ data: { clinic_listing_id: clinic.id } });
      if (response.status !== 200) {
        notifyError(response.data.detail || "Failed to submit request. Please try again.");
        return;
      }
      notifySuccess("Request submitted successfully.");
      setSelectedClinic(null);
      navigate("/clinics");
    } catch {
      notifyError("Failed to submit request. Please try again.");
    }
  };

  return (
    <div className="flex flex-col gap-3 p-6">
      <button
        onClick={() => setSelectedClinic(null)}
        className="flex flex-row items-center gap-2 w-fit hover:opacity-90 transition-opacity"
      >
        <ArrowLeftIcon className="size-4 text-rio-600" style={{ strokeWidth: 2 }} />
        <Paragraph className="text-rio-600!" displayType="loud" size="small">
          Back to search
        </Paragraph>
      </button>

      <Paragraph colorType="primary" size="medium" displayType="loud">
        Request to add {clinic.name} Practice to your organization
      </Paragraph>

      <div className="flex flex-col">
        <div className="flex flex-col p-4 gap-2 border rounded-xl bg-limestone-50">
          <div className="flex flex-col">
            <Label size="small" colorType="secondary">
              Name
            </Label>
            <Paragraph colorType="secondary">{clinic?.name}</Paragraph>
          </div>

          <div className="flex flex-col">
            <Label size="small" colorType="secondary">
              Address
            </Label>
            <Paragraph colorType="secondary">
              {formatAddress({
                addressLine1: toTitleCase(clinic?.location?.addressLine1) || undefined,
                addressLine2: toTitleCase(clinic?.location?.addressLine2) || undefined,
                city: toTitleCase(clinic?.location?.city),
                state: clinic?.location?.state,
                zip: clinic?.location?.postalCode,
              })}
            </Paragraph>
          </div>
        </div>
      </div>

      <div className="flex flex-row justify-end gap-2">
        <Button text="Cancel" appearance="secondary" onClick={() => setSelectedClinic(null)} />

        <Button text="Submit Request" onClick={handleSelect} disabled={isPending} />
      </div>
    </div>
  );
};
